import VoxelStatistics from '../../enums/VoxelStatistics.js';
import { boxAverageReductionFactors, deriveBoxAverageGrid, } from '../../utilities/voxelGrid/index.js';
import { volumeTextureBytes } from '../../utilities/getBufferConfiguration.js';
import { gridLimitsOfGpuSettings } from '../../utilities/gpuSettingsPresets.js';
import { getLossyDownsampling } from '../../init.js';
import { isMutableSlab } from '../../cache/classes/VolumeTextureSet.js';
import { FULL_RESOLUTION_TEXTURE_SET } from '../../cache/classes/ImageVolume.js';
class FullExtentTextureStrategy {
    constructor({ name, volume, grid, statistic = VoxelStatistics.Average, reductionCause, }) {
        this.active = false;
        this.name = name;
        this.volume = volume;
        this.grid = grid;
        this.statistic = statistic;
        this.reductionCause = reductionCause;
    }
    isReady() {
        return Boolean(this.volume.getTextureSet(this.name));
    }
    bindings() {
        const member = this.volume.getTextureSet(this.name)?.members()[0];
        if (!member || isMutableSlab(member)) {
            return [];
        }
        return [
            {
                texture: member.texture,
                grid: member.grid,
                statistic: member.statistic,
                role: 'base',
            },
        ];
    }
    activate() {
        const set = this.volume.getTextureSet(this.name);
        if (this.active && this.claimedSet === set) {
            return;
        }
        this.claimedSet =
            set && this.volume.claimTextureSet(this.name) ? set : undefined;
        this.active = true;
    }
    deactivate() {
        if (!this.active) {
            return;
        }
        if (this.claimedSet &&
            this.claimedSet === this.volume.getTextureSet(this.name)) {
            this.volume.releaseTextureSet(this.name);
        }
        this.claimedSet = undefined;
        this.active = false;
    }
    update() {
        if (this.active) {
            this.activate();
        }
    }
}
function componentsOf(volume) {
    return volume.voxelManager?.numberOfComponents || 1;
}
function bytesPerVoxelOf(volume) {
    return volumeTextureBytes(1, volume.dataType, componentsOf(volume));
}
export function reducedStrategyName(factors, statistic = VoxelStatistics.Average) {
    const rounded = factors.map((factor) => Number(factor.toFixed(4)));
    return `reduced-${rounded.join('x')}/full-extent/${statistic}`;
}
export function volumeTextureReductionFactors(volume, gpuSettings) {
    if (!gpuSettings ||
        !getLossyDownsampling().final ||
        volume.isDynamicVolume?.() ||
        componentsOf(volume) > 1) {
        return [1, 1, 1];
    }
    const limits = gridLimitsOfGpuSettings(gpuSettings, bytesPerVoxelOf(volume));
    return boxAverageReductionFactors(volume.voxelGrid.dimensions, limits);
}
export function reductionCauseOf(volume, gpuSettings) {
    if (!volumeTextureReductionFactors(volume, gpuSettings).some((f) => f > 1)) {
        return undefined;
    }
    const edgeFactors = boxAverageReductionFactors(volume.voxelGrid.dimensions, {
        maxEdge: gpuSettings.maxTextureEdge,
    });
    return edgeFactors.some((f) => f > 1) ? 'deviceTextureLimit' : 'memoryBudget';
}
export function provisionFullResolutionStrategy({ volume, gpuSettings, }) {
    const grid = volume.voxelGrid;
    const factors = volumeTextureReductionFactors(volume, gpuSettings);
    if (factors.some((factor) => factor > 1)) {
        return undefined;
    }
    const set = volume.provisionTextureSet({
        name: FULL_RESOLUTION_TEXTURE_SET,
        grids: [grid],
        coverage: 'full-extent',
        backstop: true,
    });
    if (!set) {
        return undefined;
    }
    return new FullExtentTextureStrategy({
        name: FULL_RESOLUTION_TEXTURE_SET,
        volume,
        grid,
    });
}
export function provisionReducedResolutionStrategy({ volume, gpuSettings, }) {
    const fullResolutionGrid = volume.voxelGrid;
    const factors = volumeTextureReductionFactors(volume, gpuSettings);
    if (!factors.some((factor) => factor > 1)) {
        return undefined;
    }
    const statistic = volume.reductionStatistic ?? VoxelStatistics.Average;
    const grid = deriveBoxAverageGrid(fullResolutionGrid, { factors });
    const name = reducedStrategyName(factors, statistic);
    const set = volume.provisionTextureSet({
        name,
        grids: [grid],
        coverage: 'full-extent',
        statistic,
    });
    if (!set) {
        return undefined;
    }
    return new FullExtentTextureStrategy({
        name,
        volume,
        grid,
        statistic,
        reductionCause: reductionCauseOf(volume, gpuSettings),
    });
}
export const defaultVolumeStrategyProvider = (context) => {
    const strategies = [];
    const full = provisionFullResolutionStrategy(context);
    if (full) {
        strategies.push(full);
        return strategies;
    }
    const reduced = provisionReducedResolutionStrategy(context);
    if (reduced) {
        strategies.push(reduced);
    }
    return strategies;
};
export const selectFirstReadyStrategy = ({ strategies, previous, }) => {
    if (previous?.isReady()) {
        return previous;
    }
    return strategies.find((strategy) => strategy.isReady());
};
